import { useCallback, useEffect, useRef, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

type Orientation = 'vertical' | 'horizontal';

type ChatSplitterProps = {
  /** 'vertical' = drag up/down to resize height. 'horizontal' = left/right to resize width. */
  orientation: Orientation;
  /** Current pixel size; callback receives new size clamped by the store. */
  size: number;
  onResize: (next: number) => void;
  /** Minimum size in px — the store also clamps. */
  min: number;
  /** Maximum size in px (relative to viewport). Default 80% of vw/vh. */
  max?: number;
};

/**
 * Thin draggable handle. Pointer-events based — works on mouse + touch.
 * Prefers the pointer capture API so drag continues off-element.
 */
export function ChatSplitter({
  orientation,
  size,
  onResize,
  min,
  max,
}: ChatSplitterProps) {
  const { t } = useTranslation('common');
  const startRef = useRef<{ pointer: number; size: number } | null>(null);

  const maxPx = useCallback(() => {
    if (max != null) return max;
    if (typeof window === 'undefined') return Number.MAX_SAFE_INTEGER;
    return orientation === 'vertical'
      ? Math.round(window.innerHeight * 0.8)
      : Math.round(window.innerWidth * 0.7);
  }, [max, orientation]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      startRef.current = {
        pointer: orientation === 'vertical' ? e.clientY : e.clientX,
        size,
      };
    },
    [orientation, size],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const start = startRef.current;
      if (!start) return;
      const current = orientation === 'vertical' ? e.clientY : e.clientX;
      // When resizing height (vertical splitter above the chat) dragging DOWN
      // should SHRINK the chat; when resizing width (splitter on chat's left)
      // dragging LEFT should GROW the chat.
      const delta =
        orientation === 'vertical' ? start.pointer - current : start.pointer - current;
      const next = Math.min(Math.max(start.size + delta, min), maxPx());
      onResize(next);
    },
    [orientation, onResize, min, maxPx],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      startRef.current = null;
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
    },
    [],
  );

  // Keyboard accessibility — arrow keys nudge 24px.
  useEffect(() => {
    // placeholder for future keyboard handler on the splitter role element
  }, []);

  const style: CSSProperties =
    orientation === 'vertical'
      ? { height: 4, cursor: 'row-resize' }
      : { width: 4, cursor: 'col-resize' };

  return (
    <div
      role="separator"
      aria-orientation={orientation}
      aria-label={t('chatSplitter.label', { defaultValue: 'Resize chat' })}
      aria-valuemin={min}
      aria-valuemax={maxPx()}
      aria-valuenow={size}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onKeyDown={(e) => {
        const step = e.shiftKey ? 48 : 24;
        if (orientation === 'vertical') {
          if (e.key === 'ArrowUp') onResize(Math.min(size + step, maxPx()));
          if (e.key === 'ArrowDown') onResize(Math.max(size - step, min));
        } else {
          if (e.key === 'ArrowLeft') onResize(Math.min(size + step, maxPx()));
          if (e.key === 'ArrowRight') onResize(Math.max(size - step, min));
        }
      }}
      style={style}
      className="shrink-0 bg-border transition-colors hover:bg-primary/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      data-testid="chat-splitter"
    />
  );
}
